import { ApiVideoAsset, BaseModel, BucketFile, GeoPoint } from './core';
import { Institution } from './rest-api';
import type { SchoolListCard } from './school-list';

export type School = BaseModel &
  SchoolAbout & {
    name?: string;
    rootId: string;
    title?: string;
    contacts?: SchoolContact[];
    address?: SchoolAddress | null;

    type?: (typeof SchoolTypes)[number];
    levels?: (typeof SchoolLevels)[number][];
    levelDescriptions?: (typeof SchoolLevelDescriptions)[number][];
    cooperatingCollegeOfEducation?: (typeof CollegesOfEducation)[number];
    institution?: Institution | null;

    whatWeOffer?: string;
    whatWeOffer_html?: string;
    whatWeOfferHighlights?: string[];
    /** label → Material Symbols icon name */
    whatWeOfferHighlightIcons?: Record<string, string>;

    pedagogicFocus?: string;
    pedagogicFocus_html?: string;
    pedagogicFocusHighlights?: string[];
    /** label → Material Symbols icon name */
    pedagogicFocusHighlightIcons?: Record<string, string>;

    media?: BucketFile[];
    documents?: BucketFile[];
    video?: ApiVideoAsset | null;

    countJobs?: number;
    verified?: boolean;
    verifiedOn?: string;
    isPublic?: boolean;

    avatar?: BucketFile | null;
    backgroundImage?: BucketFile | null;

    isRoot?: boolean;
    order?: number;
    /** From GET …/unit basic info (`parent_order`) — nav uses this for Standort chip order. */
    parentOrder?: number;
    /**
     * Parent Schuleinheit display name from GET …/detail (`unit.unit.name`).
     * Present on Standort profiles so nav can synthesize the unit row when GET …/unit is unavailable.
     */
    unitRootName?: string;
    /** Schulgemeinde row or child Schuleinheit — from listing / hierarchy */
    parentId?: string | null;
    isSchoolAuthority?: boolean;
    /** Set after GET /v1/schools/{id}/unit for the public /schools list Standorte panel */
    listUnitLoaded?: boolean;
    /** Public /schools list card; Schulgemeinden and Schuleinheiten only. */
    listCard?: SchoolListCard | null;
    locations?: SchoolLocation[];
    _geoloc?: GeoPoint;

    members?: SchoolMembership[];

    // It would be cleaner to move this property to SchoolSubscription type
    authorizedForModules?: ModulesAuthorization;
  };

export type SchoolMembership = {
  id: string;
  name: string;
  avatar?: BucketFile | null;
};

export type SchoolLocation = {
  id?: string;
  name?: string;
  type?: (typeof SchoolTypes)[number];
  isRoot?: boolean;
  isPublic?: boolean;
  order?: number;
  /** Client-only key for draft Standorte rows before the server assigns `id`. */
  tempKey?: string;
  avatar?: BucketFile | null;
  backgroundImage?: BucketFile | null;
  address?: SchoolAddress | null;
  levels?: (typeof SchoolLevels)[number][];
  levelDescriptions?: (typeof SchoolLevelDescriptions)[number][];
};

export type SchoolAbout = {
  countStudents?: string;
  countEmployees?: string;
  about?: string;
  aboutHighlights?: string[];
  /** label → Material Symbols icon name */
  aboutHighlightIcons?: Record<string, string>;
  about_html?: string;
  socialLinks?: string[] | null;
};

export type SchoolContact = {
  userId: string;
  name: string;
  email: string;
  phone: string;
  jobFunction: string;
  avatar?: BucketFile | null;
};

export type SchoolAddress = {
  street?: string;
  streetNumber?: string;
  zipcode?: string;
  city?: string;
  canton?: string;
  cantonShort?: string;
  municipality?: string; // Gemeinde
  phone?: string;
  website?: string;
  email?: string;
};

export type SchoolVerfication = BaseModel & {
  userId: string;
  email: string;
};

export const SchoolTypes = [
  'Öffentliche Schule',
  'Privatschule',
  'Sonderschule',
] as const;

export const SchoolLevels = [
  'Zyklus I',
  'Zyklus II',
  'Zyklus III',
  'Basisstufe',
  'Kindergarten',
  'Unterstufe',
  'Mittelstufe',
  'Sekundarstufe I',
  'Sekundarstufe II',
  'Primarstufe',
  'Tertiärstufe',
  'Quartärstufe',
] as const; // Stufe

export const SchoolLevelsShortMap = {
  ['Zyklus I']: 'Zyklus I',
  ['Zyklus II']: 'Zyklus II',
  ['Zyklus III']: 'Zyklus III',
  ['Basisstufe']: 'Basisstufe',
  ['Kindergarten']: 'Kiga',
  ['Unterstufe']: 'Ust',
  ['Mittelstufe']: 'Mst',
  ['Primarstufe']: 'Primar',
  ['Sekundarstufe I']: 'Sek 1',
  ['Sekundarstufe II']: 'Sek 2',
  ['Tertiärstufe']: 'Tertiärstufe',
  ['Quartärstufe']: 'Quartärstufe',
};

export const SchoolLevelDescriptions = [
  'Gymnasium',
  'Fachmittelschule',
  'Berufsmaturitätsschule',
  'Brückenangebote',
  'Berufsschule',
  'Pädagogische Hochschule',
  'Universität/ETH',
  'Fachhochschule',
  'Höhere Fachschule',
  'Höhere Berufsbildung',
  'Weiterbildung',
  'Erwachsenenbildung',
] as const;

export const CollegesOfEducation = [
  'PHZH',
  'PHLU',
  'PHBern',
  'PHSG',
  'PHSZ',
  'PHGR',
  'FHNW',
  'PH Wallis',
  'PHFR',
  'PHTG',
  'PHZG',
  'PHSH',
] as const;

export type ModulesAuthorization = {
  profile?: boolean;
  jobs?: boolean;
};

export const CollegesOfEducationInfoMap = {
  ['PHZH']: {
    name: 'PH Zürich',
    url: 'https://phzh.ch/ueber-die-phzh/berufspraxis-partnerschaften/kooperationsschulen-und-praxislehrpersonen/kooperationsschulen',
  },
  ['PHLU']: {
    name: 'PH Luzern',
    url: 'https://www.phlu.ch',
  },
  ['PHBern']: {
    name: 'PH Bern',
    url: 'https://www.phbern.ch',
  },
  ['PHSG']: {
    name: 'PH St. Gallen',
    url: 'https://www.phsg.ch',
  },
  ['PHSZ']: {
    name: 'PH Schwyz',
    url: 'https://www.phsz.ch',
  },
  ['PHGR']: {
    name: 'PH Graubünden',
    url: 'https://phgr.ch',
  },
  ['FHNW']: {
    name: 'PH der FH Nordwestschweiz',
    url: 'https://www.fhnw.ch/de/die-fhnw/hochschulen/ph',
  },
  ['PH Wallis']: {
    name: 'PH Wallis',
    url: 'https://www.hepvs.ch/de',
  },
  ['PHFR']: {
    name: 'PH Freiburg',
    url: 'https://hepfr.ch/de',
  },
  ['PHTG']: {
    name: 'PH Thurgau',
    url: 'https://www.phtg.ch',
  },
  ['PHZG']: {
    name: 'PH Zug',
    url: 'https://www.zg.ch/behoerden/direktion-fur-bildung-und-kultur/phzg',
  },
  ['PHSH']: {
    name: 'PH Schaffhausen',
    url: 'https://www.phsh.ch',
  },
};
